import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, ArrowRight, Check, Search } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo, breadcrumbSchema } from "../lib/useSeo";

export default function IndustryLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/industries/${slug}/preview`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo(data ? {
    title: `AI Prompt Shortcuts for ${data.name}`,
    description: `${data.total_commands}+ ready-made AI short-command prompts for ${data.name.toLowerCase()} — copy, add your details, paste into ChatGPT, Claude or Gemini. From £27/year.`,
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Search by Category", path: "/categories" }, { name: data.name, path: `/industry/${data.slug}` }]),
  } : {});

  const unlock = () => {
    localStorage.setItem("aish_pending_industry", JSON.stringify({ industry: data.slug, industryName: data.name, sub: "", subName: "" }));
    const isMember = user && (user.membership !== "free" || user.role === "admin");
    if (isMember) navigate(`/app?industry=${data.slug}`);
    else navigate("/pricing");
  };

  if (notFound) return <div className="p-20 text-center text-slate-500">Industry not found. <Link to="/categories" className="text-emerald-700 font-semibold">Browse all categories</Link></div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="industry-landing-page">
      <section className="bg-[#0B0F0D] text-white py-14 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-display text-[#D4AF37] font-bold text-xs sm:text-sm tracking-[0.16em] uppercase">Universal Short-Command Prompt Dictionary™</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 leading-tight">
            AI Prompt Shortcuts for<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-[#D4AF37] to-amber-500">{data.name}</span>
          </h1>
          <p className="text-gray-300 mt-4 text-sm sm:text-base max-w-xl mx-auto">{data.description} Copy a command, add your details, paste it into ChatGPT, Claude or Gemini — no prompt engineering needed.</p>
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {data.subcategories.slice(0, 8).map((s) => (
              <span key={s} className="bg-white/5 border border-white/10 text-gray-300 text-xs font-medium px-3 py-1.5 rounded-full">{s}</span>
            ))}
          </div>
          <button data-testid="industry-landing-cta" onClick={unlock}
            className="mt-8 bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-500 hover:brightness-105 text-slate-950 font-bold px-7 py-4 rounded-xl shadow-lg shadow-amber-500/25 active:scale-[0.98] transition-all text-sm sm:text-base">
            GET INSTANT ACCESS — FROM £27/YEAR
          </button>
          <p className="text-xs text-gray-500 mt-3">1-day free trial on annual plans · Instant access on mobile &amp; desktop</p>
        </div>
      </section>

      <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{data.total_commands}+ shortcuts ready for {data.name.split(",")[0].split(" &")[0].toLowerCase()} businesses</h2>
            <p className="text-slate-500 text-sm mt-2">A taste of what's inside — unlock to search, copy and use them all.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="industry-preview-commands">
            {data.commands.map((c) => (
              <div key={c.name} onClick={unlock}
                className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 flex items-center justify-between gap-2 cursor-pointer hover:border-emerald-500/40 hover:shadow-sm transition-all group">
                <div className="min-w-0">
                  <span className="cmd-chip bg-slate-900 text-amber-400 font-bold px-2.5 py-1 rounded-lg text-sm inline-block">{c.name}</span>
                  <p className="text-[11px] text-slate-400 mt-1.5 truncate">{c.category}</p>
                </div>
                <Lock size={15} className="text-slate-300 group-hover:text-emerald-600 shrink-0 transition-colors" />
              </div>
            ))}
          </div>
          <p className="text-center font-display text-[#B8962E] font-bold mt-6 tracking-wide">And Hundreds More</p>
        </div>
      </section>

      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-[#0B0F0D] rounded-3xl p-8 sm:p-10 text-white text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Search it. Copy it. Add your details. Use it.</h2>
          <ul className="mt-6 space-y-2.5 text-sm text-gray-300 max-w-md mx-auto text-left">
            {["Search everyday phrases — no technical jargon", `Shortcuts matched to ${data.name.toLowerCase()}`, "One-click copy on mobile & desktop", "Bonus Quick-Command Cheat Sheet included"].map((f) => (
              <li key={f} className="flex items-start gap-2"><Check size={16} className="text-emerald-400 mt-0.5 shrink-0" /> {f}</li>
            ))}
          </ul>
          <button data-testid="industry-landing-cta-bottom" onClick={unlock}
            className="mt-8 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold px-7 py-3.5 rounded-xl hover:brightness-105 active:scale-[0.98] transition-all">
            UNLOCK {String(data.total_commands)}+ SHORTCUTS
          </button>
          <p className="text-xs text-gray-500 mt-3">Student £27/year · Pro £47/year · Lifetime £99</p>
        </div>
        <p className="text-center mt-8">
          <Link to="/categories" data-testid="industry-back-link" className="text-emerald-700 text-sm font-semibold hover:underline inline-flex items-center gap-1"><Search size={14} /> Browse all 32 industries <ArrowRight size={14} /></Link>
        </p>
      </section>
    </div>
  );
}
